<script setup lang="ts">
/**
 * ResourceBars — CPU, Power Grid, Calibration resource usage bars
 */
import type { ResourceUsage, SlotUsage } from '../../../shared/types';

const props = defineProps<{
  cpu: ResourceUsage;
  powerGrid: ResourceUsage;
  calibration: ResourceUsage;
  slots: {
    high: SlotUsage;
    med: SlotUsage;
    low: SlotUsage;
    rig: SlotUsage;
    turret: SlotUsage;
    launcher: SlotUsage;
  };
}>();

function barPercent(r: ResourceUsage): number {
  return Math.min(r.percent, 100);
}

function barClass(r: ResourceUsage): string {
  if (r.percent > 100) return 'over';
  if (r.percent > 90) return 'warning';
  return '';
}

function fmt(n: number): string {
  if (n >= 10000) return (n / 1000).toFixed(1) + 'k';
  return n.toFixed(1);
}
</script>

<template>
  <div class="resource-bars">
    <!-- CPU -->
    <div class="resource-row" :title="`CPU: ${fmt(props.cpu.used)} / ${fmt(props.cpu.total)} tf`">
      <span class="resource-label">CPU</span>
      <div class="bar-container">
        <div
          class="bar-fill cpu-bar"
          :class="barClass(props.cpu)"
          :style="{ width: barPercent(props.cpu) + '%' }"
        ></div>
      </div>
      <span class="resource-value" :class="barClass(props.cpu)">
        {{ fmt(props.cpu.used) }} / {{ fmt(props.cpu.total) }}
      </span>
    </div>

    <!-- Power Grid -->
    <div class="resource-row" :title="`PG: ${fmt(props.powerGrid.used)} / ${fmt(props.powerGrid.total)} MW`">
      <span class="resource-label">PG</span>
      <div class="bar-container">
        <div
          class="bar-fill pg-bar"
          :class="barClass(props.powerGrid)"
          :style="{ width: barPercent(props.powerGrid) + '%' }"
        ></div>
      </div>
      <span class="resource-value" :class="barClass(props.powerGrid)">
        {{ fmt(props.powerGrid.used) }} / {{ fmt(props.powerGrid.total) }}
      </span>
    </div>

    <!-- Calibration -->
    <div class="resource-row" :title="`Calibration: ${props.calibration.used.toFixed(0)} / ${props.calibration.total.toFixed(0)}`">
      <span class="resource-label">Cal</span>
      <div class="bar-container">
        <div
          class="bar-fill cal-bar"
          :class="barClass(props.calibration)"
          :style="{ width: barPercent(props.calibration) + '%' }"
        ></div>
      </div>
      <span class="resource-value" :class="barClass(props.calibration)">
        {{ props.calibration.used.toFixed(0) }} / {{ props.calibration.total.toFixed(0) }}
      </span>
    </div>

    <!-- Slot summary -->
    <div class="slot-summary">
      <span class="slot-item" title="High Slots">
        <span class="slot-dot high"></span>{{ props.slots.high.used }}/{{ props.slots.high.total }}
      </span>
      <span class="slot-item" title="Med Slots">
        <span class="slot-dot med"></span>{{ props.slots.med.used }}/{{ props.slots.med.total }}
      </span>
      <span class="slot-item" title="Low Slots">
        <span class="slot-dot low"></span>{{ props.slots.low.used }}/{{ props.slots.low.total }}
      </span>
      <span class="slot-item" title="Rig Slots">
        <span class="slot-dot rig"></span>{{ props.slots.rig.used }}/{{ props.slots.rig.total }}
      </span>
      <span class="slot-item" title="Turret Hardpoints">
        T{{ props.slots.turret.used }}/{{ props.slots.turret.total }}
      </span>
      <span class="slot-item" title="Launcher Hardpoints">
        L{{ props.slots.launcher.used }}/{{ props.slots.launcher.total }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.resource-bars {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0;
}

.resource-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.resource-label {
  width: 24px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.bar-container {
  flex: 1;
  height: 6px;
  background: rgba(42, 63, 95, 0.4);
  border-radius: 3px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.cpu-bar { background: var(--accent-blue); }
.pg-bar { background: var(--accent-orange); }
.cal-bar { background: var(--accent-purple); }

.bar-fill.warning { background: var(--accent-orange); }
.bar-fill.over { background: var(--accent-red); }

.resource-value {
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
  min-width: 70px;
  text-align: right;
  flex-shrink: 0;
}

.resource-value.warning { color: var(--accent-orange); }
.resource-value.over { color: var(--accent-red); font-weight: 600; }

.slot-summary {
  display: flex;
  gap: 8px;
  padding: 4px 0 0;
  flex-wrap: wrap;
}

.slot-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}

.slot-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.slot-dot.high { background: var(--high-slot); }
.slot-dot.med { background: var(--med-slot); }
.slot-dot.low { background: var(--low-slot); }
.slot-dot.rig { background: var(--rig-slot); }
</style>
